<template>
  <section class="d-grid gap-3" data-test="reporte-cobranza">
    <header class="d-flex justify-content-between align-items-start flex-wrap gap-3">
      <div>
        <h2 class="h5 mb-1">Cobranza del período</h2>
        <p class="text-muted small mb-0">
          De lo que había que cobrar cada mes, cuánto entró. Se mide contra la cuota,
          no contra la caja: un pago de noviembre por la cuota de octubre mejora octubre.
        </p>
      </div>
      <button class="btn btn-outline-dark btn-sm" data-test="btn-csv"
              :disabled="loading || !filas.length" @click="exportar">
        <i class="bi bi-download me-1"></i>Exportar CSV
      </button>
    </header>

    <div class="card shadow-sm">
      <div class="card-body">
        <div class="row g-2 align-items-end">
          <div class="col-auto">
            <label class="form-label small mb-1" for="c-desde">Desde (AAAA-MM)</label>
            <input id="c-desde" v-model="filtros.periodo_desde" type="text" placeholder="2026-02"
                   class="form-control form-control-sm" data-test="input-desde">
          </div>
          <div class="col-auto">
            <label class="form-label small mb-1" for="c-hasta">Hasta (AAAA-MM)</label>
            <input id="c-hasta" v-model="filtros.periodo_hasta" type="text" placeholder="2026-12"
                   class="form-control form-control-sm" data-test="input-hasta">
          </div>
          <div class="col-auto">
            <button class="btn btn-dark btn-sm" data-test="btn-buscar"
                    :disabled="loading" @click="cargar">
              {{ loading ? 'Buscando...' : 'Buscar' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="error" class="alert alert-danger mb-0" data-test="error">{{ error }}</div>

    <div v-if="totales" class="row g-3">
      <div class="col-md-4">
        <div class="card shadow-sm"><div class="card-body">
          <div class="text-muted small">Generado</div>
          <div class="h5 mb-0" data-test="total-generado">{{ formatoMonto(totales.generado) }}</div>
        </div></div>
      </div>
      <div class="col-md-4">
        <div class="card shadow-sm"><div class="card-body">
          <div class="text-muted small">Cobrado</div>
          <div class="h5 mb-0 text-success" data-test="total-cobrado">
            {{ formatoMonto(totales.cobrado) }}
          </div>
        </div></div>
      </div>
      <div class="col-md-4">
        <div class="card shadow-sm"><div class="card-body">
          <div class="text-muted small">Tasa de cobranza</div>
          <div class="h5 mb-0" data-test="total-tasa">{{ porcentaje(totales.tasa) }}</div>
        </div></div>
      </div>
    </div>

    <div v-if="filas.length" class="card shadow-sm">
      <div class="card-body">
        <h3 class="h6 mb-2">Cobrado y pendiente por período</h3>
        <GraficoReporte tipo="combo" :series="series" :categorias="categorias" />
      </div>
    </div>

    <div class="card shadow-sm">
      <div class="table-responsive">
        <table class="table table-sm mb-0" data-test="tabla">
          <thead>
            <tr>
              <th>Período</th><th class="text-end">Cuotas</th>
              <th class="text-end">Generado</th><th class="text-end">Cobrado</th>
              <th class="text-end">Pendiente</th><th class="text-end">Tasa</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="f in filas" :key="f.periodo">
              <td>{{ f.periodo }}</td>
              <td class="text-end">{{ f.cantidad }}</td>
              <td class="text-end">{{ formatoMonto(f.generado) }}</td>
              <td class="text-end text-success">{{ formatoMonto(f.cobrado) }}</td>
              <td class="text-end">{{ formatoMonto(f.pendiente) }}</td>
              <td class="text-end">{{ porcentaje(f.tasa) }}</td>
            </tr>
            <tr v-if="!filas.length && !loading">
              <td colspan="6" class="text-center text-muted py-4" data-test="vacio">
                No hay cuotas generadas en ese rango de períodos.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </section>
</template>

<script setup>
// N1 — Cobranza del período. RG-F5. SPEC RN-84 · PR-2.

import { ref, computed, onMounted } from 'vue'
import reportsService from '@/services/reportsService'
import GraficoReporte from '@/components/GraficoReporte.vue'

const filas = ref([])
const totales = ref(null)
const loading = ref(false)
const error = ref('')
const filtros = ref({ periodo_desde: '', periodo_hasta: '' })

const categorias = computed(() => filas.value.map((f) => f.periodo))
const series = computed(() => [
  { name: 'Cobrado', type: 'column', data: filas.value.map((f) => Number(f.cobrado)) },
  { name: 'Pendiente', type: 'column', data: filas.value.map((f) => Number(f.pendiente)) },
  { name: 'Tasa', type: 'line', data: filas.value.map((f) => Number(f.tasa)) }
])

const formatoMonto = (v) =>
  new Intl.NumberFormat('es-PY', { maximumFractionDigits: 0 }).format(Number(v) || 0)
const porcentaje = (v) => `${Math.round((Number(v) || 0) * 100)}%`

async function cargar () {
  loading.value = true
  error.value = ''
  try {
    const data = await reportsService.getCobranzaPeriodo(filtros.value)
    filas.value = data.filas
    totales.value = data.totales
  } catch (e) {
    error.value = e?.response?.data?.message || 'No se pudo cargar el reporte.'
  } finally {
    loading.value = false
  }
}

async function exportar () {
  try {
    await reportsService.descargarCsv('cobranza-periodo', filtros.value, 'cobranza-periodo')
  } catch (e) {
    error.value = 'No se pudo exportar el CSV.'
  }
}

onMounted(cargar)
</script>
